import type { CmsSidebarSectionFragment } from "@workspace/casino_core/queries/CmsSidebarQuery";
import CmsPhrase from "@workspace/cms-phrases/src/phrase/CmsPhrase";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@workspace/frontend-ui";
import LinkComponent from "@workspace/nextjs_utils/src/components/LinkComponent";
import type { Dispatch, FC, ReactNode, SetStateAction } from "react";
import DynamicHugeIcon from "../../../../utils/DynamicHugeIcon";
import SidebarIcon from "./SidebarIcon";
import isSidebarItemVisible from "./utils/isSidebarItemVisible";

type Item = NonNullable<CmsSidebarSectionFragment["items"]>[number];

type Props = {
  item: Item;
  itemKey: string;
  brandSlug: string;
  isLoggedIn: boolean;
  isExpanded: boolean;
  isMobile: boolean;
  dynamicSubitems?: ReactNode;
  setExpandedItemKey: Dispatch<SetStateAction<string | null>>;
  enabledFeatureFlags?: string[];
};

type Subitem = NonNullable<Item["subitems"]>[number];

const renderLeafLikeItem = (
  menuItem: Pick<Item, "id" | "href" | "icon" | "hugeIcon" | "title" | "slug">,
  key: string,
  brandSlug: string,
) => (
  <SidebarMenuSubItem key={key} data-cms-item={menuItem.slug}>
    <SidebarMenuSubButton
      asChild
      className="data-active:text-sidebar-primary hover:text-sidebar-primary h-10 rounded-xl text-sm"
    >
      <LinkComponent href={menuItem.href ?? ""}>
        <SidebarIcon
          icon={menuItem.icon}
          hugeIcon={menuItem.hugeIcon}
          size={20}
        />
        <CmsPhrase phrase={menuItem.title} brandSlug={brandSlug} />
      </LinkComponent>
    </SidebarMenuSubButton>
  </SidebarMenuSubItem>
);

const SidebarCollapsibleGroupItem: FC<Props> = ({
  item,
  itemKey,
  brandSlug,
  isLoggedIn,
  isExpanded,
  isMobile,
  dynamicSubitems,
  setExpandedItemKey,
  enabledFeatureFlags,
}) => {
  const { setOpenMobile } = useSidebar();
  const enabledFeatureFlagSet = new Set(enabledFeatureFlags);
  const visibleSubitems = (item.subitems ?? []).filter((subitem) =>
    isSidebarItemVisible(
      subitem.visibility,
      subitem.active,
      isLoggedIn,
      subitem.featureFlag,
      enabledFeatureFlagSet,
    ),
  );
  const hasSubitems = visibleSubitems.length > 0 || Boolean(dynamicSubitems);

  const subitemsContent = (
    <SidebarMenuSub className="m-0 gap-1 border-0 p-0">
      {item.subitemsTitle && (
        <div className="px-2 py-1 text-xs font-medium uppercase text-muted-foreground">
          <CmsPhrase
            phrase={item.subitemsTitle}
            brandSlug={brandSlug}
            className="text-xs"
          />
        </div>
      )}
      {visibleSubitems.map((subitem: Subitem, subIndex) => {
        const subitemKey =
          subitem.id ??
          subitem.slug ??
          subitem.href ??
          `${itemKey}-sub-${subIndex}`;

        return renderLeafLikeItem(subitem, subitemKey, brandSlug);
      })}
      {dynamicSubitems}
    </SidebarMenuSub>
  );

  if (!hasSubitems) {
    return renderLeafLikeItem(item, itemKey, brandSlug);
  }

  if (!isMobile) {
    return (
      <HoverCard key={itemKey} openDelay={200} closeDelay={200}>
        <HoverCardTrigger asChild>
          <SidebarMenuSubItem data-cms-item={item.slug}>
            <SidebarMenuSubButton
              asChild
              className="data-active:text-sidebar-primary hover:text-sidebar-primary h-10 rounded-xl text-sm"
            >
              <LinkComponent href={item.href ?? ""} className="w-full">
                <SidebarIcon
                  icon={item.icon}
                  hugeIcon={item.hugeIcon}
                  size={20}
                />
                <CmsPhrase phrase={item.title} brandSlug={brandSlug} />
                <DynamicHugeIcon
                  iconName="ArrowDown01Icon"
                  size={18}
                  className="ml-auto -rotate-90"
                />
              </LinkComponent>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        </HoverCardTrigger>

        <HoverCardContent
          side="right"
          align="start"
          sideOffset={8}
          className="w-72 rounded-xl border border-border bg-secondary p-1.5 overflow-scroll max-h-[90vh]"
        >
          {subitemsContent}
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <Collapsible
      key={itemKey}
      open={isExpanded}
      onOpenChange={(open) => {
        if (isMobile) {
          setExpandedItemKey(open ? itemKey : null);
        }
      }}
      asChild
      className="group/collapsible"
    >
      <SidebarMenuSubItem data-cms-item={item.slug}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="w-full"
            onClick={() => {
              setOpenMobile(false);
            }}
          >
            <SidebarMenuSubButton
              asChild
              className="data-active:text-sidebar-primary hover:text-sidebar-primary h-10 flex-1 rounded-xl text-sm"
            >
              <LinkComponent href={item.href ?? ""} className="w-full">
                <SidebarIcon
                  icon={item.icon}
                  hugeIcon={item.hugeIcon}
                  size={20}
                />
                <CmsPhrase phrase={item.title} brandSlug={brandSlug} />
              </LinkComponent>
            </SidebarMenuSubButton>
          </button>

          <CollapsibleTrigger asChild>
            <Button
              className="rounded-xl bg-foreground/6 text-foreground p-1.75 mr-1"
              aria-label="Toggle submenu"
            >
              <DynamicHugeIcon
                iconName="ArrowDown01Icon"
                size={18}
                className={cn(
                  "transition-transform duration-200",
                  isExpanded && "rotate-180",
                )}
              />
            </Button>
          </CollapsibleTrigger>
        </div>

        <CollapsibleContent>
          <div className="ml-3 mt-1">{subitemsContent}</div>
        </CollapsibleContent>
      </SidebarMenuSubItem>
    </Collapsible>
  );
};

export default SidebarCollapsibleGroupItem;
